import { useState } from 'react';

// Every numeric setting a runtime's manifest exposes, grouped as the manifest groups them.
// Values start at the runtime's defaults (or the run's current settings); only changed
// values are reported, so a run inherits future defaults for everything left alone.

const GROUPS = [
  ['shape', 'shape', 'the model\'s form · fixed once it exists'],
  ['dynamics', 'dynamics', 'learning and io2'],
  ['hidden', 'advanced', 'controller constants'],
  ['reporting', 'reporting', 'dreams and saves'],
];

const field = 'bg-transparent border border-[#383f38] rounded-md px-2 py-1 text-white font-mono text-xs outline-none focus:border-white transition-colors w-24';

export function defaultsOf(fields) {
  return Object.fromEntries(fields.map(f => [f.key, f.value]));
}

export default function RunSettings({ fields, current = {}, onChange, lockShape = false, hide = [], startOpen = false }) {
  const [open, setOpen] = useState(startOpen);
  const [values, setValues] = useState(() => ({ ...defaultsOf(fields), ...Object.fromEntries(Object.entries(current).map(([k, v]) => [k, String(v)])) }));
  const baseline = { ...defaultsOf(fields), ...Object.fromEntries(Object.entries(current).map(([k, v]) => [k, String(v)])) };

  const set = (key, value) => {
    const next = { ...values, [key]: value };
    setValues(next);
    const changed = {};
    for (const f of fields) {
      if (hide.includes(f.key) || (lockShape && f.group === 'shape')) continue;
      const v = next[f.key];
      if (v === undefined || v === '' || String(v) === String(baseline[f.key])) continue;
      const n = Number(v);
      if (!Number.isNaN(n)) changed[f.key] = f.type === 'integer' ? Math.round(n) : n;
    }
    onChange(changed);
  };

  const changedCount = fields.filter(f => String(values[f.key]) !== String(baseline[f.key])).length;
  const visible = fields.filter(f => !hide.includes(f.key));
  if (!visible.length) return null;

  return (
    <div>
      <button type="button" onClick={() => setOpen(!open)}
        className="font-mono text-xs text-[#888888] hover:text-white lowercase">
        {open ? '▾' : '▸'} settings{changedCount ? ` · ${changedCount} changed` : Object.keys(current).length ? ' · as the model was saved' : ' · runtime defaults'}
      </button>
      {open && (
        <div className="mt-3 space-y-4">
          {GROUPS.map(([group, title, note]) => {
            const items = visible.filter(f => f.group === group);
            if (!items.length) return null;
            const locked = lockShape && group === 'shape';
            return (
              <div key={group}>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="font-mono text-xs text-white lowercase">{title}</span>
                  <span className="font-mono text-[10px] text-[#555555] lowercase">{locked ? 'kept from the model' : note}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                  {items.map(f => {
                    const changed = String(values[f.key]) !== String(baseline[f.key]);
                    return (
                      <label key={f.key} className="flex items-center justify-between gap-3 font-mono text-xs">
                        <span className={changed ? 'text-white' : 'text-[#888888]'} title={f.key}>{f.label || f.key}</span>
                        <span className="flex items-center gap-1.5">
                          {changed && <button type="button" onClick={() => set(f.key, baseline[f.key])}
                            className="text-[#555555] hover:text-white" title="back to default">↺</button>}
                          <input value={values[f.key] ?? ''} disabled={locked} inputMode="decimal"
                            list={f.options ? `opts-${f.key}` : undefined}
                            onChange={e => set(f.key, e.target.value)}
                            className={`${field} ${locked ? 'opacity-40' : ''} ${changed ? 'border-white' : ''}`} />
                          {f.options && <datalist id={`opts-${f.key}`}>{f.options.map(o => <option key={o} value={o} />)}</datalist>}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
